"use client";

import Image from "next/image";
import { MessageCircle, X } from "lucide-react";
import { useMemo, useState } from "react";

import { Button } from "~/components/ui/button";

type OnboardingHireBuilderWidgetProps = {
  partnerUrl: string;
  agentName: string;
  avatarUrl: string;
  accentColor?: string;
};

export function OnboardingHireBuilderWidget({
  partnerUrl,
  agentName,
  avatarUrl,
  accentColor = "#4989F7",
}: OnboardingHireBuilderWidgetProps) {
  const [open, setOpen] = useState(false);
  const iframeSrc = useMemo(() => {
    const base =
      typeof window !== "undefined"
        ? `${window.location.origin}${partnerUrl}`
        : partnerUrl;
    const separator = base.includes("?") ? "&" : "?";
    return `${base}${separator}channel=agent_profile`;
  }, [partnerUrl]);

  return (
    <>
      {!open ? (
        <button
          type="button"
          data-onboarding-widget-launcher
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-50 flex items-center gap-2 rounded-full px-4 py-3 text-sm font-semibold text-white shadow-lg transition hover:opacity-90"
          style={{ backgroundColor: accentColor }}
        >
          {avatarUrl ? (
            <span className="relative h-8 w-8 overflow-hidden rounded-full border border-white/30">
              <Image src={avatarUrl} alt="" fill className="object-cover" sizes="32px" />
            </span>
          ) : (
            <MessageCircle className="h-5 w-5" />
          )}
          Ask {agentName.split(" ")[0]}
        </button>
      ) : null}

      {open ? (
        <>
          <div
            className="fixed inset-0 z-50 bg-black/30"
            onClick={() => setOpen(false)}
            aria-hidden
          />
          <div className="fixed bottom-6 right-6 z-50 flex h-[min(720px,calc(100vh-3rem))] w-[min(420px,calc(100vw-2rem))] flex-col overflow-hidden rounded-2xl border border-border bg-background shadow-2xl">
            <div className="flex items-center justify-between border-b border-border px-4 py-3">
              <div className="flex items-center gap-2 min-w-0">
                <span className="relative h-8 w-8 shrink-0 overflow-hidden rounded-full">
                  <Image src={avatarUrl} alt="" fill className="object-cover" sizes="32px" />
                </span>
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold">{agentName}</p>
                  <p className="text-xs text-muted-foreground">Onboarding assistant</p>
                </div>
              </div>
              <Button variant="ghost" size="icon" onClick={() => setOpen(false)} aria-label="Close">
                <X className="h-4 w-4" />
              </Button>
            </div>
            <iframe title={`Chat with ${agentName}`} src={iframeSrc} className="flex-1 w-full border-0" />
          </div>
        </>
      ) : null}
    </>
  );
}
